import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { ROLES, type Role } from "@vital/shared";
import { useAuth, useActiveOrganization } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { InspectPanel } from "../components/InspectPanel";
import { OpsEmptyState, SectionHeader, InventorySkeleton } from "../components/ops-display";
import {
  UserInspectSummary,
  UsersFilterBar,
  UsersInventoryList,
} from "../components/users-inventory";
import { api, ApiError } from "../lib/api";
import { cmsStrings } from "../terminology";
import {
  USER_ROLE_LABELS,
  USERS_PAGE_NOTE,
  filterAndSortUsers,
  type UserListRow,
  type UserSortKey,
} from "../lib/users-ops";

export function UsersPage() {
  const { user } = useAuth();
  const activeOrg = useActiveOrganization();
  const organizationLabel =
    activeOrg.code || activeOrg.displayName || activeOrg.organizationId || "—";

  const [users, setUsers] = useState<UserListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState("");
  const [listFilter, setListFilter] = useState("all");
  const [sort, setSort] = useState<UserSortKey>("name");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>("editor");
  const [password, setPassword] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");

  const [deactivateTarget, setDeactivateTarget] = useState<UserListRow | null>(null);
  const [deactivatePassword, setDeactivatePassword] = useState("");
  const [deactivating, setDeactivating] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await api.listUsers());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری ناموفق");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedId && !users.some((u) => u.id === selectedId)) {
      setSelectedId(null);
    }
  }, [users, selectedId]);

  const filtered = useMemo(
    () =>
      filterAndSortUsers(users, {
        query,
        filter: listFilter,
        sort,
      }),
    [users, query, listFilter, sort],
  );

  const selected = useMemo(
    () => users.find((u) => u.id === selectedId) ?? null,
    [users, selectedId],
  );

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.createUser({ email, displayName, role, password, ownerPassword });
      setSuccess("کاربر ایجاد شد.");
      setEmail("");
      setDisplayName("");
      setPassword("");
      setOwnerPassword("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ایجاد ناموفق");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate(e: FormEvent) {
    e.preventDefault();
    if (!deactivateTarget) return;
    setDeactivating(true);
    setError(null);
    try {
      await api.updateUser(deactivateTarget.id, {
        active: false,
        ownerPassword: deactivatePassword,
      });
      setSuccess(`«${deactivateTarget.displayName}» غیرفعال شد.`);
      setDeactivateTarget(null);
      setDeactivatePassword("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "عملیات ناموفق");
    } finally {
      setDeactivating(false);
    }
  }

  function renderUserActions(row: UserListRow): ReactNode {
    if (!row.active || row.id === user?.id) return null;
    return (
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => {
          setDeactivateTarget(row);
          setDeactivatePassword("");
        }}
      >
        غیرفعال
      </button>
    );
  }

  if (user?.role !== "owner") {
    return (
      <>
        <PageHeader title={cmsStrings.nav.users} />
        <p className="banner banner--error">فقط مالک به این بخش دسترسی دارد.</p>
      </>
    );
  }

  return (
    <>
      <PageHeader title={cmsStrings.nav.users} />

      <aside
        className="ops-safety-summary ops-safety-summary--privacy"
        role="note"
        data-vt-users-safety
      >
        <p className="ops-safety-summary__eyebrow">حریم دسترسی</p>
        <ul className="ops-safety-summary__points">
          <li>فقط مالک (Owner) به این صفحه دسترسی دارد</li>
          <li>بدون رمز، توکن MFA، یا دادهٔ نشست در فهرست یا جزئیات</li>
        </ul>
        <details className="ops-safety-disclosure">
          <summary>متن کامل حریم و دسترسی</summary>
          <p>{USERS_PAGE_NOTE}</p>
        </details>
      </aside>

      {error && <p className="banner banner--error">{error}</p>}
      {success && <p className="banner banner--success">{success}</p>}

      <div className="page-stack page-stack--inventory">
        <div
          className={`content-ops-layout${selected ? " content-ops-layout--inspect-open" : ""}`}
          data-vt-content-ops
          data-vt-inspect-open={selected ? "true" : "false"}
        >
          <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
            <SectionHeader title="فهرست کاربران" />
            <UsersFilterBar
              filter={listFilter}
              onFilterChange={setListFilter}
              query={query}
              onQueryChange={setQuery}
              sort={sort}
              onSortChange={setSort}
              organizationLabel={organizationLabel}
            />
            {loading ? (
              <InventorySkeleton />
            ) : users.length === 0 ? (
              <OpsEmptyState title="کاربری ثبت نشده است" />
            ) : filtered.length === 0 ? (
              <OpsEmptyState title={cmsStrings.inventory.noMatchTitle} />
            ) : (
              <UsersInventoryList
                rows={filtered}
                selectedId={selectedId}
                currentUserId={user?.id}
                onSelect={setSelectedId}
                renderActions={renderUserActions}
              />
            )}
          </Card>
          {selected && (
            <InspectPanel title="جزئیات کاربر" className="content-inspect-panel">
              <UserInspectSummary
                item={selected}
                organizationLabel={organizationLabel}
                isSelf={selected.id === user?.id}
                actions={renderUserActions(selected)}
              />
            </InspectPanel>
          )}
        </div>

        <Card title="ایجاد کاربر">
          <form className="content-form content-form--in-card" onSubmit={handleCreate}>
            <label>
              <span>ایمیل</span>
              <input
                type="email"
                dir="ltr"
                className="ltr-isolate"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              <span>نام نمایشی</span>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </label>
            <label>
              <span>نقش</span>
              <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {USER_ROLE_LABELS[r] ?? r}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>رمز اولیه</span>
              <input
                type="password"
                dir="ltr"
                className="ltr-isolate"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
                autoComplete="new-password"
              />
            </label>
            <label>
              <span>رمز عبور مالک (تأیید)</span>
              <input
                type="password"
                dir="ltr"
                className="ltr-isolate"
                value={ownerPassword}
                onChange={(e) => setOwnerPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </label>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? "در حال ایجاد…" : "ایجاد کاربر"}
            </button>
          </form>
        </Card>
      </div>

      {deactivateTarget && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => setDeactivateTarget(null)}
        >
          <div
            className="modal"
            role="dialog"
            aria-labelledby="deactivate-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="deactivate-title">تأیید غیرفعال‌سازی</h2>
            <p>کاربر «{deactivateTarget.displayName}» دیگر نمی‌تواند وارد شود.</p>
            <form onSubmit={handleDeactivate}>
              <label>
                <span>رمز عبور مالک برای تأیید</span>
                <input
                  type="password"
                  dir="ltr"
                  className="ltr-isolate"
                  value={deactivatePassword}
                  onChange={(e) => setDeactivatePassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </label>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setDeactivateTarget(null)}
                >
                  انصراف
                </button>
                <button type="submit" className="btn btn--danger" disabled={deactivating} aria-busy={deactivating}>
                  {deactivating ? "در حال پردازش…" : "غیرفعال کردن"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
